import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { styled } from "styled-components";
import { Button } from "./Button";
import { CountdownTimer } from "./CountdownTimer";
import { colors } from "../colors";
import { Show } from "./Show";

const HeaderImage = styled.img`
  width: 100%;
  height: auto;
`;

const FooterContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export type AlertMessageTypes = {
  title: string;
  description: string;
  image: string;
  buttonLabel?: string;
  themeColor?: string;
  expiration?: string;
  campaignId: string;
};

export const AlertMessage = (props: AlertMessageTypes) => {
  const { title, description, image, buttonLabel, themeColor, expiration, campaignId } =
    props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  useEffect(() => {
    // Create a unique key for this alert based on campaign and content
    // If any of these change, it's treated as a new alert
    const alertKey = `alert-seen-${campaignId}-${title}-${description}`;

    // Check if user has seen this alert in current session
    const hasSeenAlert = sessionStorage.getItem(alertKey);

    if (!hasSeenAlert) {
      onOpen();
    }
  }, [onOpen, campaignId, title, description]);

  const handleClose = () => {
    // Mark this alert as seen for the current session
    const alertKey = `alert-seen-${campaignId}-${title}-${description}`;
    sessionStorage.setItem(alertKey, 'true');
    onClose();
  };

  return (
    <>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent m="auto">
            <HeaderImage src={image} />
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              style={{ paddingBottom: "0.5rem" }}
            >
              <FooterContentWrapper>
                {title}
                <Show when={!!expiration}>
                  <CountdownTimer
                    colorTheme={themeColor ?? colors.black}
                    expirationUtc={expiration!}
                    isInAlertMessage
                  />
                </Show>
              </FooterContentWrapper>
            </AlertDialogHeader>
            <AlertDialogBody>{description}</AlertDialogBody>
            <AlertDialogFooter>
              <Button
                buttonType="primary"
                themeColor={themeColor}
                onClick={handleClose}
                label={buttonLabel ?? "OK"}
              />
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
